import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowRight, CaretLeft, CaretRight, Phone } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { HERO_SLIDES, BRAND } from "../lib/data";

export default function HeroSlider() {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 28 });
    const [selected, setSelected] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelected(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on("select", onSelect);
        const id = setInterval(() => emblaApi.scrollNext(), 6500);
        return () => {
            clearInterval(id);
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi, onSelect]);

    return (
        <section data-testid="hero-slider" className="relative w-full h-[90vh] min-h-[640px] overflow-hidden bg-brand-primary">
            <div ref={emblaRef} className="overflow-hidden h-full">
                <div className="flex h-full touch-pan-y">
                    {HERO_SLIDES.map((slide, idx) => (
                        <div key={idx} className="relative flex-[0_0_100%] min-w-0 h-full overflow-hidden">
                            <div
                                className="absolute inset-0 bg-cover bg-center animate-slow-zoom"
                                style={{ backgroundImage: `url(${slide.url})` }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/30" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Overlayed copy */}
            <div className="absolute inset-0 flex items-end pb-24 sm:items-center sm:pb-0">
                <div className="max-w-7xl w-full mx-auto px-4 sm:px-8">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selected}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="max-w-3xl"
                        >
                            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#D6E0CF] mb-5 hero-text-shadow">
                                {HERO_SLIDES[selected].eyebrow}
                            </p>
                            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight whitespace-pre-line hero-text-shadow">
                                {HERO_SLIDES[selected].title}
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-[#E5E3D8] max-w-xl leading-relaxed hero-text-shadow">
                                {HERO_SLIDES[selected].sub}
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
                                <Link
                                    to="/contact"
                                    data-testid="hero-quote-cta"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brand-bg text-brand-primary text-sm font-semibold hover:bg-white transition-all hover:-translate-y-0.5"
                                >
                                    Get a Free Quote <ArrowRight size={18} weight="bold" />
                                </Link>
                                <a
                                    href={`tel:${BRAND.officePhone.replace(/\D/g, "")}`}
                                    data-testid="hero-call-cta"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brand-accent text-white text-sm font-semibold hover:bg-[#a25e43] transition-all hover:-translate-y-0.5"
                                >
                                    <Phone size={18} weight="duotone" /> {BRAND.officePhone}
                                </a>
                                <Link
                                    to="/gallery"
                                    data-testid="hero-gallery-cta"
                                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white text-sm font-semibold hover:bg-white/10 transition-all"
                                >
                                    View Our Work
                                </Link>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Controls */}
            <div className="absolute bottom-7 right-4 sm:right-8 flex items-center gap-3 z-10">
                <button
                    type="button"
                    onClick={() => emblaApi && emblaApi.scrollPrev()}
                    data-testid="hero-prev"
                    className="p-3 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors"
                    aria-label="Previous slide"
                >
                    <CaretLeft size={18} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={() => emblaApi && emblaApi.scrollNext()}
                    data-testid="hero-next"
                    className="p-3 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors"
                    aria-label="Next slide"
                >
                    <CaretRight size={18} weight="bold" />
                </button>
            </div>

            {/* Dots */}
            <div className="absolute bottom-9 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {HERO_SLIDES.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => emblaApi && emblaApi.scrollTo(i)}
                        data-testid={`hero-dot-${i}`}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`h-1.5 rounded-full transition-all ${i === selected ? "w-10 bg-white" : "w-4 bg-white/40"}`}
                    />
                ))}
            </div>
        </section>
    );
}
